import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  CalendarMonth as CalendarMonthIcon,
  Pending as PendingIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';

const EarningsOverview = ({
  earnings,
  title = "Earnings Overview",
  showPayoutAction = true,
  hasStripeConnectAccount = false,
  isStripeConnectValid = false
}) => {
  if (!earnings) {
    return null;
  }

  const handlePayoutRequest = () => {
    // Handle payout request internally
    toast.success('Payout request submitted successfully!');
    // Here you could also make an API call to process the payout
    console.log('Payout requested for:', earnings.pendingPayouts);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        {title}
      </Typography>
      
      {/* Earnings Stats Cards - Using platform's common card pattern */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 2
      }}>
        {/* Total Earned Card */}
        <Card sx={{
          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          color: 'white',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TrendingUpIcon sx={{ mr: 1, fontSize: 24 }} />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total Earned
              </Typography>
            </Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              ${earnings.totalEarned || 0}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              All time
            </Typography>
          </CardContent>
        </Card>

        {/* This Month Card */}
        <Card sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CalendarMonthIcon sx={{ mr: 1, fontSize: 24 }} />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                This Month
              </Typography>
            </Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              ${earnings.thisMonth || 0}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Current month
            </Typography>
          </CardContent>
        </Card>

        {/* Pending Payouts Card */}
        <Card sx={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PendingIcon sx={{ mr: 1, fontSize: 24 }} />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Pending Payouts
              </Typography>
            </Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              ${earnings.pendingPayouts || 0}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Available for transfer
            </Typography>
          </CardContent>
        </Card>

        {/* Last Payout Card */}
        <Card sx={{
          background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
          color: 'white',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalanceIcon sx={{ mr: 1, fontSize: 24 }} />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Last Payout
              </Typography>
            </Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              ${earnings.lastPayout || 0}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Most recent transfer
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Payout Action Card - Only show if Stripe Connect account exists and is valid */}
      {showPayoutAction && earnings.pendingPayouts > 0 && hasStripeConnectAccount && isStripeConnectValid && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2
            }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Available for Payout
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Request a payout to transfer your earnings to your bank account
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="success"
                startIcon={<AccountBalanceIcon />}
                onClick={handlePayoutRequest}
                sx={{ 
                  minWidth: 'fit-content',
                  px: 4,
                  py: 1.5
                }}
              >
                Request Payout
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default EarningsOverview;
